import { FEATURE_FLAGS } from '~featureFlags'
import { useFeatureGate } from '~hooks/access/useFeatureGate'
import {
  type APIConnection,
  type APIEndpoint,
  INTEGRATION_TYPES,
  type IntegrationDefinition,
  getIntegrationById,
} from '~pages/KnowledgeBaseScreen/components/integrations'
import { type APIAutomation } from '~types'

export interface IntegrationEndpointEntry {
  connection: APIConnection
  endpoint: APIEndpoint
}

export type IntegrationBlockMetadata = NonNullable<APIAutomation['metadata']>

export interface IntegrationEndpointOption {
  key: string
  name: string
  description: string | null
  icon: string
  metadata: IntegrationBlockMetadata
}

export interface IntegrationSection {
  definition: IntegrationDefinition
  options: IntegrationEndpointOption[]
}

const WORKFLOW_INTEGRATION_TYPES = [
  INTEGRATION_TYPES.OPENGOV,
  INTEGRATION_TYPES.CITYWORKS,
  INTEGRATION_TYPES.CENTRAL_SQUARE,
]

export const useEnabledIntegrationTypes = (): Set<string> => {
  const enabledByType = {
    [INTEGRATION_TYPES.OPENGOV]: useFeatureGate(FEATURE_FLAGS.INTEGRATION_BLOCKS).enabled,
    [INTEGRATION_TYPES.CITYWORKS]: useFeatureGate(FEATURE_FLAGS.CITYWORKS_INTEGRATION_BLOCK)
      .enabled,
    [INTEGRATION_TYPES.CENTRAL_SQUARE]: useFeatureGate(
      FEATURE_FLAGS.CENTRAL_SQUARE_INTEGRATION_BLOCK,
    ).enabled,
  }
  return new Set(
    WORKFLOW_INTEGRATION_TYPES.filter((integrationType) => enabledByType[integrationType]),
  )
}

export const getConnectionIcon = (connection: APIConnection): string | undefined =>
  getIntegrationById(connection.connection_type ?? '')?.icon

export const getEndpointKey = (endpoint: APIEndpoint): string => endpoint.tool_name ?? endpoint.name

export const buildEndpointLookup = (
  connections: APIConnection[],
): Record<APIEndpoint['id'], IntegrationEndpointEntry> =>
  Object.fromEntries(
    connections.flatMap((connection) =>
      connection.endpoints.map((endpoint) => [endpoint.id, { connection, endpoint }]),
    ),
  )

export const getWorkflowEnabledEndpointKeys = (connections: APIConnection[]): Set<string> =>
  new Set(
    connections.flatMap((connection) =>
      connection.endpoints
        .filter((endpoint) =>
          endpoint.configs.some(
            (config) => config.integration_surface === 'WORKFLOW' && config.active,
          ),
        )
        .map((endpoint) => `${connection.connection_type}:${getEndpointKey(endpoint)}`),
    ),
  )

export const getMetadataEndpoint = (
  metadata: APIAutomation['metadata'],
): { name: string; icon: string } | null => {
  if (!metadata) return null
  const definition = getIntegrationById(metadata.connection_type)
  const endpoint = definition?.endpoints?.find(
    (candidate) => candidate.toolName === metadata.endpoint_tool_name,
  )
  return definition && endpoint ? { name: endpoint.name, icon: definition.icon } : null
}

export const INTEGRATION_SECTIONS: IntegrationSection[] = WORKFLOW_INTEGRATION_TYPES.flatMap(
  (integrationType) => {
    const definition = getIntegrationById(integrationType)
    if (!definition) return []
    const options = (definition.endpoints ?? []).map((endpoint) => ({
      key: `${definition.id}:${endpoint.toolName}`,
      name: endpoint.name,
      description: endpoint.description,
      icon: definition.icon,
      metadata: { connection_type: definition.id, endpoint_tool_name: endpoint.toolName },
    }))
    return [{ definition, options }]
  },
)
